import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/react';
import categoriesApi, {
  Category,
  CategoryTreeNode,
  GetCategoriesParams,
  CreateCategoryRequest,
} from '../../services/apis/categories';

export type { Category, CategoryTreeNode };

export interface CategoryFormData {
  name: string;
  description?: string;
  parentId?: string;
}

export const categoryKeys = {
  all: ['categories'] as const,
  lists: (params?: GetCategoriesParams) => [...categoryKeys.all, 'list', params] as const,
  detail: (id: string) => [...categoryKeys.all, 'detail', id] as const,
};

/**
 * Get categories. When parentOnly is true, returns only root categories as flat list.
 * When parentOnly is false/undefined, returns all categories as hierarchical tree.
 */
export const useCategories = (params?: GetCategoriesParams) => {
  return useQuery({
    queryKey: categoryKeys.lists(params),
    queryFn: async () => {
      const response = await categoriesApi.getCategories(params);
      return (response.data as Category[]) || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Get categories as a hierarchical tree structure.
 * This is an alias for useCategories without parentOnly filter.
 */
export const useCategoryTree = () => {
  return useQuery({
    queryKey: categoryKeys.lists({ parentOnly: false }),
    queryFn: async () => {
      const response = await categoriesApi.getCategories({ parentOnly: false });
      return (response.data as CategoryTreeNode[]) || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCategory = ({ id, shouldSkip }: { id: string; shouldSkip: boolean }) => {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: async () => {
      const response = await categoriesApi.getCategory(id);
      return response.data!;
    },
    enabled: !!id && !shouldSkip,
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CategoryFormData) => {
      const response = await categoriesApi.createCategory(data as CreateCategoryRequest);
      return response.data as Category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      addToast({
        title: 'Category created successfully',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error creating category:', error);
      addToast({
        title: error.message || 'Failed to create category',
        color: 'danger',
      });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CategoryFormData }) => {
      const response = await categoriesApi.updateCategory(id, data);
      return response.data as Category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      addToast({
        title: 'Category updated successfully',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error updating category:', error);
      addToast({
        title: 'Failed to update category',
        color: 'danger',
      });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await categoriesApi.deleteCategory(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      addToast({
        title: 'Category deleted successfully',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error deleting category:', error);
      addToast({
        title: 'Failed to delete category',
        color: 'danger',
      });
    },
  });
};
